import { useState } from "react";

import { isAxiosError } from "axios";

import {
  FEATURE_GATE_METADATA,
  getFeatureGateTitle,
} from "src/adminPanel/featureGateMetadata";
import { usePatchWorkspace } from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { Checkbox } from "src/base-components/Checkbox";
import { Label } from "src/base-components/Label";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  selectedWs: WorkspaceDataplane;
};

export const ModifyWsFeatureGatesModal: React.FC<Props> = ({
  isOpen,
  onClose,
  selectedWs,
}) => {
  const [featureGates, setFeatureGates] = useState(
    selectedWs.feature_gates ?? {},
  );
  const [errors, setErrors] = useState<string>("");

  const patchWs = usePatchWorkspace();
  // once a feature gate is enabled it can't be disabled anymmore, so we disable the submit button if all are enabled
  const disableSubmit = Object.values(selectedWs.feature_gates ?? {}).every(
    (value) => value === true,
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header>Modify Workspace Feature Gates</Modal.Header>
      <Modal.Content>
        {Object.entries(featureGates).map(([key, value]) => (
          <div key={key} className="my-2 flex items-center space-x-2">
            <Checkbox
              checked={value}
              disabled={selectedWs.feature_gates?.[key]}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFeatureGates((prev) => ({
                  ...prev,
                  [key]: event.target.checked,
                }));
              }}
            />
            <Label mb="mb-0">
              {getFeatureGateTitle(key)}
              {key in FEATURE_GATE_METADATA && (
                <span className="font-inter-normal-13px text-gray-500">
                  {" "}
                  ({key})
                </span>
              )}
            </Label>
          </div>
        ))}
        {featureGates.cm_copilot && !selectedWs.feature_gates?.cm_copilot && (
          <Callout type="info">
            After enabling CM Copilot, go to the Agents module and set the
            following quota for{" "}
            <span className="font-inter-medium-12px">claude-4-6-sonnet</span>:{" "}
            {`{"monthly_tokens": 120000000, "tpm": 100000, "rpm": 200}`}
          </Callout>
        )}
        <ErrorHint>{errors}</ErrorHint>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="submit"
            disabled={disableSubmit}
            loading={patchWs.isPending}
            variant="primary"
            onClick={async () => {
              const workspace = selectedWs;
              workspace.feature_gates = featureGates;
              try {
                await patchWs.mutateAsync(workspace);
                onClose();
              } catch (e) {
                if (isAxiosError(e)) {
                  setErrors(
                    JSON.stringify(e.response?.data?.detail) ?? e.message,
                  );
                } else {
                  setErrors(JSON.stringify(e));
                }
              }
            }}
          >
            Submit
          </Button>
        }
      />
    </Modal>
  );
};
